import { HierarchyCard, SafeHtmlContent } from '@yes.technology/react-toolkit'

import { FormEventHandler, useCallback } from 'react'
import {
  Activity as ActivityType,
  FlatInteractionModelData,
  Step
} from 'types/shared'
import Activity from './ActivityInput/Activity'
import Review from './Review'
import SelectAndLinkFrame from './SelectAndLinkFrame/SelectAndLinkFrame'
import { useAppSelector } from 'state/store'
import { GroupValidationContextProvider } from './ActivityInput/ActivityGroup/GroupValidationContext'

export type InteractionStepProps = {
  totalSteps: number
  currentStepIndex: number
  activityModels: FlatInteractionModelData['activity_models']
  activityValues: FlatInteractionModelData['activity_values']
  steps: Step[]
  handleChangeStep: (step: number) => void
  handleChangeActivity: (activity: ActivityType) => void
}

const InteractionStep = ({
  totalSteps,
  currentStepIndex,
  steps,
  activityModels,
  activityValues,
  handleChangeStep,
  handleChangeActivity
}: InteractionStepProps) => {
  const changeActivity = useCallback(
    (id: string, newValue: { id: string; des: string } | string) => {
      const activityValue =
        typeof newValue === 'object' ? newValue.id : newValue

      handleChangeActivity({
        uuid_activity_model: id,
        des: activityValue,
        complement: typeof newValue === 'object' ? newValue.des : undefined
      })
    },
    [handleChangeActivity]
  )

  const onSave = () => {
    handleChangeStep(totalSteps + 1)
  }

  const onSubmit: FormEventHandler = (event) => {
    event.preventDefault()
    handleChangeStep(currentStepIndex + 1)
  }

  const currentStep = steps[currentStepIndex - 1]

  const { flatData, userName, dateStartInteraction, geolocation } =
    useAppSelector(({ interactions }) => interactions.interactionModel)

  return (
    <div style={{ maxWidth: 608 }} className='m-auto mt-3 mb-3'>
      {currentStepIndex < totalSteps && (
        <SelectAndLinkFrame>
          <GroupValidationContextProvider>
            <HierarchyCard>
              {currentStep && currentStep.des && <h2>{currentStep.des}</h2>}

              {currentStep?.explanation_text && (
                <div className='mb-3'>
                  <SafeHtmlContent html={currentStep?.explanation_text} />
                </div>
              )}

              {currentStep?.complement && <p>{currentStep.complement}</p>}

              {currentStep && Array.isArray(currentStep.activity_models) && (
                <form onSubmit={onSubmit}>
                  {currentStep.activity_models.map((activityModelUuid) => {
                    const value = activityValues[activityModelUuid]

                    return (
                      <Activity
                        key={activityModelUuid}
                        activityModelInput={activityModels[activityModelUuid]}
                        value={Array.isArray(value) ? undefined : value}
                        changeActivity={changeActivity}
                        currentStepIndex={currentStepIndex}
                        disabled={activityModels[activityModelUuid]?.disabled}
                      />
                    )
                  })}
                  {/* https://stackoverflow.com/questions/4196681/form-not-submitting-when-pressing-enter */}
                  {/* ...When there is only a single text field in a form, pressing 'enter' in the text field
                will automatically submit; but if there is more than one (regular (i.e. single-line)
                text input) field, it does nothing, unless there is also some kind of 'submit' button. */}
                  <input type='submit' style={{ display: 'none' }} />
                </form>
              )}
            </HierarchyCard>
          </GroupValidationContextProvider>
        </SelectAndLinkFrame>
      )}

      {currentStepIndex === totalSteps && flatData && (
        <Review
          onSave={onSave}
          goToStep={handleChangeStep}
          flatData={flatData}
          userName={userName}
          dateStartInteraction={dateStartInteraction}
          geolocation={geolocation}
        />
      )}
    </div>
  )
}

export default InteractionStep
